/**
 * Documentation Layout Component
 *
 * Layout for public documentation pages with navigation sidebar,
 * language selector, and responsive design.
 */

import { useState, type ComponentType } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Home, BookOpen, Globe, Monitor, Cpu, Shield, Trash2, FileText, Store, ChevronDown, ExternalLink, SlidersHorizontal, Sun, Moon } from 'lucide-react';
import { useI18n, useLocalizedPath, localeNames, Locale } from '@/lib/i18n';
import { useSwitchLocale } from '@/lib/locale-sync';
import { splitLocalePath } from '@/lib/locale-path';
import { Logo } from '@/components/ui/Logo';
import { SokujiGlyph } from '@/components/ui/SokujiGlyph';
import { SiteFooter } from '@/components/site/SiteFooter';
import { useTheme } from '@/lib/theme';
import '@/styles/site.scss';
import './DocsLayout.scss';

interface NavItem {
  path: string;
  icon: ComponentType<{ size?: string | number }>;
  labelKey: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { path: '/docs', icon: Home, labelKey: 'nav.docs' },
  {
    path: '/docs/install',
    icon: BookOpen,
    labelKey: 'nav.install',
    children: [
      { path: '/docs/install/windows', icon: Monitor, labelKey: 'install.windows' },
      { path: '/docs/install/macos', icon: Monitor, labelKey: 'install.macos' },
      { path: '/docs/install/linux', icon: Monitor, labelKey: 'install.linux' },
    ],
  },
  {
    path: '/docs/kizuna-ai',
    icon: SokujiGlyph,
    labelKey: 'nav.kizunaAI',
    children: [
      { path: '/docs/kizuna-ai/quick-start', icon: SokujiGlyph, labelKey: 'tutorials.kizunaQuickStart' },
      { path: '/docs/kizuna-ai/understand-others', icon: SokujiGlyph, labelKey: 'tutorials.understandOthers' },
      { path: '/docs/kizuna-ai/be-understood', icon: SokujiGlyph, labelKey: 'tutorials.beUnderstood' },
      { path: '/docs/kizuna-ai/subtitle-my-speech', icon: SokujiGlyph, labelKey: 'tutorials.subtitleMySpeech' },
      { path: '/docs/kizuna-ai/two-way-meeting', icon: SokujiGlyph, labelKey: 'tutorials.twoWayMeeting' },
      { path: '/docs/kizuna-ai/two-way-subtitles', icon: SokujiGlyph, labelKey: 'tutorials.twoWaySubtitles' },
      { path: '/docs/kizuna-ai/face-to-face', icon: SokujiGlyph, labelKey: 'tutorials.faceToFace' },
    ],
  },
  {
    path: '/docs/kizuna-ai-features',
    icon: SokujiGlyph,
    labelKey: 'nav.kizunaAIFeatures',
    children: [
      { path: '/docs/kizuna-ai/voices', icon: SokujiGlyph, labelKey: 'tutorials.kizunaVoices' },
      { path: '/docs/kizuna-ai/vocabulary', icon: SokujiGlyph, labelKey: 'tutorials.kizunaVocabulary' },
      { path: '/docs/kizuna-ai/region', icon: SokujiGlyph, labelKey: 'tutorials.kizunaRegion' },
      { path: '/docs/kizuna-ai/latency', icon: SokujiGlyph, labelKey: 'tutorials.kizunaLatency' },
      { path: '/docs/kizuna-ai/billing', icon: SokujiGlyph, labelKey: 'tutorials.kizunaBilling' },
    ],
  },
  {
    path: '/docs/features',
    icon: SlidersHorizontal,
    labelKey: 'nav.basicFeatures',
    children: [
      { path: '/docs/features/translation-modes', icon: SlidersHorizontal, labelKey: 'nav.feature.translationModes' },
      { path: '/docs/features/participant-audio', icon: SlidersHorizontal, labelKey: 'nav.participantAudio' },
      { path: '/docs/features/virtual-microphone', icon: SlidersHorizontal, labelKey: 'nav.feature.virtualMicrophone' },
      { path: '/docs/features/subtitle-mode', icon: SlidersHorizontal, labelKey: 'nav.subtitleMode' },
      { path: '/docs/features/caption-sharing', icon: SlidersHorizontal, labelKey: 'nav.feature.captionSharing' },
      { path: '/docs/features/text-only', icon: SlidersHorizontal, labelKey: 'nav.feature.textOnly' },
      { path: '/docs/features/display', icon: SlidersHorizontal, labelKey: 'nav.feature.display' },
      { path: '/docs/features/pronunciation-readings', icon: SlidersHorizontal, labelKey: 'nav.feature.pronunciationReadings' },
      { path: '/docs/features/export', icon: SlidersHorizontal, labelKey: 'nav.feature.export' },
      { path: '/docs/features/replay', icon: SlidersHorizontal, labelKey: 'nav.feature.replay' },
      { path: '/docs/features/noise-suppression', icon: SlidersHorizontal, labelKey: 'nav.feature.noiseSuppression' },
      { path: '/docs/features/passthrough', icon: SlidersHorizontal, labelKey: 'nav.feature.passthrough' },
      { path: '/docs/features/switch-devices', icon: SlidersHorizontal, labelKey: 'nav.feature.switchDevices' },
      { path: '/docs/features/diagnostic-logs', icon: SlidersHorizontal, labelKey: 'nav.feature.diagnosticLogs' },
      { path: '/docs/features/interface-language', icon: SlidersHorizontal, labelKey: 'nav.feature.interfaceLanguage' },
    ],
  },
  {
    path: '/docs/supported-sites',
    icon: Globe,
    labelKey: 'nav.platforms',
    children: [
      { path: '/docs/supported-sites', icon: Globe, labelKey: 'nav.platformsOverview' },
      { path: '/docs/tutorials/zoom', icon: Monitor, labelKey: 'tutorials.zoom' },
      { path: '/docs/tutorials/google-meet', icon: Monitor, labelKey: 'tutorials.googleMeet' },
      { path: '/docs/tutorials/microsoft-teams', icon: Monitor, labelKey: 'tutorials.teams' },
      { path: '/docs/tutorials/discord', icon: Monitor, labelKey: 'tutorials.discord' },
      { path: '/docs/tutorials/slack', icon: Monitor, labelKey: 'tutorials.slack' },
      { path: '/docs/tutorials/jitsi', icon: Monitor, labelKey: 'tutorials.jitsi' },
      { path: '/docs/tutorials/whereby', icon: Monitor, labelKey: 'tutorials.whereby' },
      { path: '/docs/tutorials/gather', icon: Monitor, labelKey: 'tutorials.gather' },
    ],
  },
  {
    path: '/docs/ai-providers',
    icon: Cpu,
    labelKey: 'nav.aiProviders',
    children: [
      { path: '/docs/ai-providers', icon: Cpu, labelKey: 'nav.providersOverview' },
      { path: '/docs/tutorials/openai-setup', icon: Cpu, labelKey: 'tutorials.openai' },
      { path: '/docs/tutorials/gemini-setup', icon: Cpu, labelKey: 'tutorials.gemini' },
      { path: '/docs/tutorials/palabraai-setup', icon: Cpu, labelKey: 'tutorials.palabraai' },
      { path: '/docs/tutorials/volcengine-ast2-setup', icon: Cpu, labelKey: 'tutorials.volcengineAST2' },
      { path: '/docs/tutorials/soniox-setup', icon: Cpu, labelKey: 'tutorials.soniox' },
      { path: '/docs/tutorials/qwen-setup', icon: Cpu, labelKey: 'tutorials.qwen' },
      { path: '/docs/tutorials/local-inference-setup', icon: Cpu, labelKey: 'tutorials.localInference' },
      { path: '/docs/local-models', icon: Cpu, labelKey: 'tutorials.localModels' },
      { path: '/docs/tutorials/realtime-api-tester', icon: Cpu, labelKey: 'tutorials.realtimeTester' },
    ],
  },
  { path: '/docs/privacy', icon: Shield, labelKey: 'nav.privacy' },
  { path: '/docs/delete-account', icon: Trash2, labelKey: 'dashboard.security.deleteAccount' },
  { path: '/docs/terms', icon: FileText, labelKey: 'nav.terms' },
  { path: '/docs/commerce', icon: Store, labelKey: 'nav.commerce' },
];

export function DocsLayout() {
  const location = useLocation();
  const { t, locale } = useI18n();
  const lp = useLocalizedPath();
  const switchLocale = useSwitchLocale();
  // The page path without its locale prefix, which is what navItems list.
  const pagePath = splitLocalePath(location.pathname).path;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [expandedSections, setExpandedSections] = useState<string[]>(['/docs/install', '/docs/kizuna-ai', '/docs/kizuna-ai-features', '/docs/supported-sites', '/docs/ai-providers', '/docs/features']);

  const toggleSection = (path: string) => {
    setExpandedSections((prev) =>
      prev.includes(path)
        ? prev.filter((p) => p !== path)
        : [...prev, path]
    );
  };

  const isActive = (path: string) => {
    if (path === '/docs') {
      return pagePath === '/docs';
    }
    return pagePath.startsWith(path);
  };

  const handleLocaleChange = (newLocale: Locale) => {
    switchLocale(newLocale);
    setLangMenuOpen(false);
  };

  return (
    <div className="docs-layout">
      {/* Header */}
      <header className="docs-layout__header">
        <div className="docs-layout__header-content">
          <button
            className="docs-layout__menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to={lp('/')} className="docs-layout__logo">
            <Logo size={25} />
            <span>Sokuji</span>
          </Link>

          <nav className="docs-layout__header-nav">
            <Link to={lp('/')} className="docs-layout__header-link">
              {t('nav.home')}
            </Link>
            <Link to={lp('/docs')} className="docs-layout__header-link docs-layout__header-link--active">
              {t('nav.docs')}
            </Link>
            <a
              href="https://github.com/kizuna-ai-lab/sokuji"
              target="_blank"
              rel="noopener noreferrer"
              className="docs-layout__header-link"
            >
              GitHub
              <ExternalLink size={14} />
            </a>
          </nav>

          <div className="docs-layout__header-actions">
            <button
              type="button"
              className="docs-layout__icon-btn"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              aria-label={isDark ? 'Light theme' : 'Dark theme'}
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {/* The code rather than the language's name, as the public header
                does it: the names are in the menu it opens. */}
            <div className="docs-layout__lang-menu">
              <button
                type="button"
                className="docs-layout__chip"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                aria-expanded={langMenuOpen}
              >
                {locale.toUpperCase()}
                <ChevronDown size={13} />
              </button>

              {langMenuOpen && (
                <div className="docs-layout__lang-dropdown">
                  {(Object.keys(localeNames) as Locale[]).map((loc) => (
                    <button
                      key={loc}
                      className={`docs-layout__lang-option ${loc === locale ? 'docs-layout__lang-option--active' : ''}`}
                      onClick={() => handleLocaleChange(loc)}
                    >
                      {localeNames[loc]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="docs-layout__divider" aria-hidden="true" />

            <Link to="/sign-in" className="docs-layout__sign-in">
              {t('common.signIn')}
            </Link>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`docs-layout__sidebar ${sidebarOpen ? 'docs-layout__sidebar--open' : ''}`}>
        <nav className="docs-layout__nav">
          {/* Sign-in entry for the widths where the header CTA is hidden. It
              sits above the nav because the nav is long enough to scroll. */}
          <Link
            to="/sign-in"
            className="docs-layout__mobile-cta"
            onClick={() => setSidebarOpen(false)}
          >
            {t('common.signIn')}
          </Link>
          <div className="docs-layout__mobile-divider" />

          {navItems.map((item) => {
            const Icon = item.icon;
            // A group is active when one of its own pages is open. Matching the
            // group's path as a prefix would light up both KizunaAI groups,
            // whose pages share /docs/kizuna-ai/.
            const active = item.children
              ? item.children.some((child) => child.path === pagePath)
              : isActive(item.path);
            const hasChildren = item.children && item.children.length > 0;
            const expanded = expandedSections.includes(item.path);

            return (
              <div key={item.path} className="docs-layout__nav-group">
                {hasChildren ? (
                  <>
                    <button
                      className={`docs-layout__nav-item docs-layout__nav-item--parent ${active ? 'docs-layout__nav-item--active' : ''}`}
                      onClick={() => toggleSection(item.path)}
                    >
                      <Icon size={18} />
                      <span>{t(item.labelKey)}</span>
                      <ChevronDown
                        size={16}
                        className={`docs-layout__nav-chevron ${expanded ? 'docs-layout__nav-chevron--open' : ''}`}
                      />
                    </button>
                    {expanded && (
                      <div className="docs-layout__nav-children">
                        {item.children?.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = pagePath === child.path;

                          return (
                            <Link
                              key={child.path}
                              to={lp(child.path)}
                              className={`docs-layout__nav-item docs-layout__nav-item--child ${childActive ? 'docs-layout__nav-item--active' : ''}`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <ChildIcon size={16} />
                              <span>{t(child.labelKey)}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={lp(item.path)}
                    className={`docs-layout__nav-item ${active ? 'docs-layout__nav-item--active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon size={18} />
                    <span>{t(item.labelKey)}</span>
                  </Link>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="docs-layout__overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="docs-layout__main">
        <div className="docs-layout__content">
          <Outlet />
        </div>

        {/* Footer */}
        <SiteFooter />
      </main>
    </div>
  );
}
